import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import type { Money } from '@libs/trip-planner-interfaces';

import type {
  ActivityStaticData,
  ActivityDynamicData,
} from '../../activityDetails';
import type { ActivityDetailsGalleryData, Distance } from '../models';
import type { ActivityDetailsMainData } from '../models/activityDetailsMain';
import type { SelectedActivityTicketsInfo } from '../models/activityDetailsSummary';

import { getDistances } from './activityDetailsStoreLogic';

export const useActivityDetailsStore = defineStore(
  'activityDetailsStore',
  () => {
    // State
    const activityStaticData = ref<ActivityStaticData | undefined>(undefined);
    const activityDynamicData = ref<ActivityDynamicData | undefined>(undefined);

    // Actions
    function setActivityStaticData(
      initialActivityStaticData: ActivityStaticData
    ): void {
      activityStaticData.value = initialActivityStaticData;
    }

    function setActivityDynamicData(
      initialActivityDynamicData: ActivityDynamicData
    ): void {
      activityDynamicData.value = initialActivityDynamicData;
    }

    // Getters
    const isLoading = computed(
      () =>
        !activityStaticData.value ||
        !activityDynamicData.value ||
        isFeatureToggleActive('loader')
    );

    const destinationCity = computed<string | undefined>(
      () =>
        activityStaticData.value?.staticInfo.mainPolygon.fullName.split(',')[0]
    );

    const galleryData = computed<ActivityDetailsGalleryData>(() => ({
      images: activityStaticData.value?.staticInfo.images,
      productName: activityStaticData.value?.staticInfo.name,
      productCode: activityStaticData.value?.productCode,
    }));

    const mainData = computed<ActivityDetailsMainData>(() => ({
      activityName: activityStaticData.value?.staticInfo.name,
      fullDestination:
        activityStaticData.value?.staticInfo.mainPolygon.fullName,
      rating: activityStaticData.value?.staticInfo.semboSunRating,
      mainDescription:
        activityStaticData.value?.staticInfo.openingParagraph.value,
    }));

    const description = computed<string | undefined>(() => {
      const description =
        activityStaticData.value?.staticInfo?.description?.value;
      if (!description) {
        return;
      }
      let formattedDescription = description;
      if (mainData.value.mainDescription) {
        formattedDescription = description.replace(
          mainData.value.mainDescription,
          ''
        );
      }
      return formattedDescription;
    });

    const distances = computed<Distance[] | undefined>(() =>
      getDistances(activityStaticData.value)
    );

    const activityTicketsLowestPrice = computed<Money | undefined>(() => {
      const prices = activityDynamicData.value?.availability.flatMap(
        (dayTicket) =>
          dayTicket.rates.flatMap((rate) =>
            rate.ageRanges.map((ageRange) => ageRange.price)
          )
      );
      if (!prices || !prices.length) {
        return;
      }
      const lowestPrice = prices.reduce((min, price) =>
        price.amount < min.amount ? price : min
      );
      return lowestPrice;
    });

    // TODO: lmg
    const selectedActivityTicketsInfo: SelectedActivityTicketsInfo = {
      selectedTicketTypes: [],
      totalPrice: undefined,
    };
    // const selectedActivityTicketsInfo = {
    //   selectedTicketTypes: [
    //     {
    //       amount: 2,
    //       date: ' 2 june, 2025',
    //       time: '08:15',
    //       ticketRate: 'Adult - Ages 16 to 99',
    //     },
    //   ],
    //   totalPrice: {amount: 599, currency: 'SEK', currencySymbol: 'Kr'},
    // };

    return {
      // State
      activityStaticData,
      activityDynamicData,

      // Actions
      setActivityStaticData,
      setActivityDynamicData,

      // Getters
      isLoading,
      destinationCity,
      galleryData,
      mainData,
      description,
      distances,
      activityTicketsLowestPrice,
      selectedActivityTicketsInfo,
    };
  }
);
